import type { User } from '../../types';

import { cache } from '../cache';
import { ApiError, estClient } from './estClient';

type NewUserProps = Pick<
  User,
  'username' | 'note' | 'email' | 'group_id' | 'password'
>;

type MutationResponse =
  | {
      status: true;
      result: User;

      /**
       * The payload that was sent to server without xsrf token
       */
      post: { [key: string]: any };
    }
  | {
      status: false;

      /**
       * Will contain error text in case status: false
       */
      message: string;
    };

export interface UserGroup {
  id: string;
  name: string;

  /**
   * Whether this role has administrative rights
   */
  admin: boolean;

  /**
   * Whether this role can be assigned to some user
   */
  assignable: boolean;
}

class UserApi {
  getAll({ cache: useCache = true }: { cache?: boolean } = {}): Promise<
    User[]
  > {
    return cache.get(
      '/api/user/all',
      async () => estClient.get<User[]>('/api/user/all'),
      { cache: useCache },
    );
  }

  /**
   * Returns currently logged in user
   */
  async getMe(): Promise<User> {
    return cache.get('user-me', async () => {
      const all = await this.getAll();

      return all.find((item) => item.current)!;
    });
  }

  async create(user: NewUserProps) {
    const resp = await estClient.post<MutationResponse>('/users/create', {
      query: {
        act: 'submit',
      },
      bodyFormat: 'url-encoded',
      body: user,
    });

    if (!resp.status) {
      throw new ApiError({
        body: {
          ...resp,
          success: resp.status,
        },
      });
    }

    this.getAll({ cache: false });
  }

  async update({ user_id, ...user }: User) {
    const resp = await estClient.post<MutationResponse>(
      `/users/update/${user_id}`,
      {
        query: {
          act: 'submit',
        },
        bodyFormat: 'url-encoded',
        body: user,
      },
    );

    if (!resp.status) {
      throw new ApiError({
        body: {
          ...resp,
          success: resp.status,
        },
      });
    }

    this.getAll({ cache: false });
  }

  async activate(userId: string) {
    await estClient.get(`/user_restore/${userId}`);
    this.getAll({ cache: false });
  }

  async deactivate(userId: string) {
    await estClient.get(`/user_dismiss/${userId}`);
    this.getAll({ cache: false });
  }

  async enableWebPush({ token }: { token: string }) {
    await estClient.post('/api/user/webpush', {
      body: {
        device_token: token,
      },
    });
  }

  async disableWebPush({
    token = '*',
  }: {
    /**
     * '*' means unsubscribe all tokens
     */
    token?: string;
  }) {
    await estClient.delete('/api/user/webpush', {
      body: {
        device_token: token,
      },
    });
  }

  async getGroups(): Promise<UserGroup[]> {
    // TODO: need to fetch this list from API when it will be available
    return [
      {
        id: '1',
        name: 'S-Admin',
        admin: true,
        assignable: false,
      },
      {
        id: '2',
        name: 'Admin',
        admin: true,
        assignable: true,
      },
      {
        id: '3',
        name: 'User',
        admin: false,
        assignable: true,
      },
    ];
  }

  get GROUPS() {
    return {
      SUPER_ADMIN: '1',
      ADMIN: '2',
      USER: '3',
    } as const;
  }

  get ROLE() {
    return {
      USER: '0',
      TRAINEE: '1',
    } as const;
  }
}

export const userApi = new UserApi();
